// Dashboard tile: resume-health for the Job Board (Phase 1).
// Counts of new-this-week / awaiting-review / parked-and-stale resumes,
// each linking straight into the Job Board with a `?filter=` cue.
// Visible to super admin + any staff base_role (matches the backend gate).

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Briefcase, ChevronRight, Sparkles, Inbox, Clock } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { useAuth } from "@/context/AuthContext";

export function ResumeHealthWidget() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const eligible =
    user?.role === "super_admin" || user?.base_role === "staff";

  useEffect(() => {
    if (!eligible) { setLoading(false); return; }
    (async () => {
      try {
        const { data: d } = await api.get("/jobs/resume-health");
        setData(d);
      } catch { setData(null); }
      finally { setLoading(false); }
    })();
  }, [eligible]);

  if (!eligible || loading || !data) return null;
  // Iter 79: non-super users without any collaborator postings see nothing.
  if (data.has_access === false) return null;

  const items = [
    { key: "new",     label: "New this week",     count: data.new_this_week,
      icon: Sparkles, filter: "new" },
    { key: "awaiting", label: "Awaiting review",  count: data.awaiting_review,
      icon: Inbox, filter: "awaiting" },
    { key: "stale",   label: `Parked · > ${data.stale_after_days}d`,
      count: data.parked_stale, icon: Clock, filter: "parked-stale" },
  ];

  const allZero = items.every((i) => i.count === 0);

  return (
    <Card data-testid="resume-health-widget"
          className="border-slate-200 shadow-sm overflow-hidden">
      <div className="flex items-center gap-2 border-b border-slate-100 px-5 py-3">
        <Briefcase className="h-4 w-4 text-indigo-500" />
        <h3 className="font-heading text-sm font-semibold text-slate-800">
          Resume health
        </h3>
        <span className="ml-2 rounded bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600"
              data-testid="resume-health-openings">
          {data.open_postings} open {data.open_postings === 1 ? "posting" : "postings"}
        </span>
        <button onClick={() => navigate("/jobs")}
                data-testid="resume-health-open-board"
                className="ml-auto inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1 text-[11px] font-medium text-primary-foreground hover:bg-primary/90 shadow-sm">
          Open Job Board <ChevronRight className="h-3 w-3" />
        </button>
      </div>
      <div className="grid grid-cols-1 gap-2 px-5 py-4 sm:grid-cols-3">
        {items.map((it) => {
          const Icon = it.icon;
          const zero = it.count === 0;
          const bg = it.key === "stale" && !zero
            ? "border-rose-200 bg-rose-50/60 hover:bg-rose-50"
            : zero
              ? "border-slate-200 bg-slate-50/60 hover:bg-slate-50"
              : "border-indigo-200 bg-indigo-50/60 hover:bg-indigo-50";
          const iconClass = it.key === "stale" && !zero
            ? "text-rose-500"
            : zero
              ? "text-slate-400"
              : "text-indigo-500";
          return (
            <button key={it.key}
                    onClick={() => navigate(`/jobs?filter=${it.filter}`)}
                    data-testid={`resume-health-${it.key}`}
                    className={`group flex items-center gap-3 rounded-md border p-3 text-left transition ${bg}`}>
              <Icon className={`h-5 w-5 shrink-0 ${iconClass}`} />
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-slate-700 dark:text-slate-800 font-semibold truncate">
                  {it.label}
                </div>
                <div className="text-xl font-semibold tabular-nums text-slate-900"
                     data-testid={`resume-health-${it.key}-count`}>
                  {it.count}
                </div>
              </div>
              <ChevronRight className="h-4 w-4 shrink-0 text-slate-300 group-hover:text-slate-500 transition" />
            </button>
          );
        })}
      </div>
      {allZero && data.total_resumes === 0 && (
        <div className="border-t border-slate-100 bg-slate-50 px-5 py-2 text-[11px] text-slate-500"
             data-testid="resume-health-empty">
          No resumes uploaded yet — head to the Job Board to add your first posting.
        </div>
      )}
    </Card>
  );
}
